import axios from "axios";
import { showErrorSnackBar, showSuccessSnackBar } from "@/utils/SnackBartHelper";
import store from '@/store/index';

// URL da API externa de faturamento //
const BILLING_API_URL = "https://api.fozbank.com.br/infrastructure/billing-order";

// API Token - TODO: Mover para variável de ambiente
const API_TOKEN = "c32475385d27128047fe7eaa9ccd2be195a97b2bc031db894f158725b9246f1b";

// Configuração: Habilitar/Desabilitar envio para API externa
// Quando true: envia para API e atualiza para 'billed' quando confirmado
// Quando false: apenas cria a ordem no Firebase com status 'billing_requested' (sem enviar para API)
const ENABLE_API_SEND = false;

/**
 * Formata valor monetário para o formato da API (ex: "21100,00" = R$ 211,00)
 * O formato é: valor em centavos com vírgula como separador decimal
 * @param {Number|String} value - Valor em reais (ex: 211.00)
 * @returns {String} Valor formatado em centavos com vírgula (ex: "21100,00")
 */
const formatCurrencyForApi = (value) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return "0,00";
  
  // Converter reais para centavos
  const cents = Math.round(numValue * 100);
  
  // Formatar: separar milhares com ponto e decimais com vírgula
  const centsString = cents.toString();
  const length = centsString.length;
  
  if (length <= 2) {
    // Menos de 1 real: "30,00" = R$ 0,30
    return `${centsString.padStart(2, '0')},00`;
  } else {
    // Mais de 1 real: separar parte inteira e decimais
    const integerPart = centsString.slice(0, -2);
    const decimalPart = centsString.slice(-2);
    
    // Adicionar pontos para separar milhares
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    return `${formattedInteger},${decimalPart}`;
  }
};

/**
 * Converte dados da proposta em items para a API
 * @param {Object} proposalData - Dados da proposta
 * @returns {Array} Array de items formatados
 */
const buildItemsFromProposal = (proposalData) => {
  const items = [];
  
  // Processar soluções
  if (proposalData.listSolutions && Array.isArray(proposalData.listSolutions)) {
    proposalData.listSolutions.forEach(solution => {
      if (solution.selected !== false && solution.total) {
        items.push({
          code: solution.solutionCod,
          quantity: solution.quantity || 1,
          exclusiveForFranchisee: solution.exclusiveForFranchisee || false,
          priceCents: formatCurrencyForApi(solution.total)
        });
      }
    });
  }
  
  // Processar serviços
  if (proposalData.listServices && Array.isArray(proposalData.listServices)) {
    proposalData.listServices.forEach(service => {
      if (service.selected !== false && service.total) {
        items.push({
          code: service.code || service.id || `SERVICE_${service.id_doc || Date.now()}`,
          quantity: service.quantity || 1,
          exclusiveForFranchisee: service.exclusiveForFranchisee || false,
          priceCents: formatCurrencyForApi(service.total)
        });
      }
    });
  }
  
  // Processar produtos
  if (proposalData.listProducts && Array.isArray(proposalData.listProducts)) {
    proposalData.listProducts.forEach(product => {
      if (product.selected !== false && product.total) {
        items.push({
          code: product.code || product.id || `PRODUCT_${product.id_doc || Date.now()}`,
          quantity: product.quantity || 1,
          exclusiveForFranchisee: product.exclusiveForFranchisee || false,
          priceCents: formatCurrencyForApi(product.total)
        });
      }
    });
  }
  
  return items;
};

// Criar instância axios para a API externa
const defaultHeaders = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

// Adicionar header ngrok apenas se a URL contiver ngrok
if (BILLING_API_URL.includes('ngrok')) {
  defaultHeaders['ngrok-skip-browser-warning'] = 'true';
}

const billingApiService = axios.create({
  baseURL: BILLING_API_URL,
  timeout: 60000,
  headers: defaultHeaders
});

// Interceptor para tratamento de erros
billingApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.message === "Network Error") {
      showErrorSnackBar("Sem conexão com a internet. Verifique sua rede e tente novamente.");
    } else if (!error.response || error.response.status >= 500) {
      showErrorSnackBar("Ocorreu um erro no servidor de faturamento. Tente novamente mais tarde.");
      console.error("Billing API error", error);
    } else if (error.response) {
      const errorMessage = error.response.data?.message || error.response.data?.error || "Erro ao processar faturamento";
      showErrorSnackBar(errorMessage);
    }
    throw error;
  }
);

const BillingOrderService = {
  /**
   * Verifica se o envio para API está habilitado
   * @returns {Boolean} true se o envio está habilitado, false caso contrário
   */
  isApiSendEnabled: () => {
    return ENABLE_API_SEND;
  },
  
  /**
   * Envia a ordem de faturamento para a API externa
   * @param {Object} billingData - Dados da ordem de faturamento
   * @param {Object} proposalData - Dados da proposta (opcional, para gerar items)
   * @returns {Promise<Array>} [success, data] - Retorna array com status de sucesso e dados da resposta
   */
  sendBillingOrder: async (billingData, proposalData = null) => {
    try {
      console.log('billingData', billingData);
      // Obter idUser do store
      const currentUser = store.state.currentUser;
      const idUser = currentUser?.uid || currentUser?.id || "1";
      
      // Mapear entryType para formato da API
      const mapEntryType = (entryType) => {
        const mapping = {
          'percentage': 'PERCENT',
          'value': 'VALUE',
          'none': 'NONE'
        };
        return mapping[entryType] || 'NONE';
      };
      
      // Construir items a partir da proposta
      const items = proposalData ? buildItemsFromProposal(proposalData) : [];
      
      // Preparar installments
      let installments = [];
      if (billingData.billingType === 'installments' && billingData.installments) {
        installments = billingData.installments.map(inst => ({
          isEntrance: inst.type === 'entry',
          amount: formatCurrencyForApi(inst.value),
          dueDate: inst.dueDate
        }));
      }
      
      // Preparar payload conforme formato da API
      const payload = {
        apiToken: API_TOKEN,
        idFranchisee: idUser,
        items: items,
        client: {
          name: billingData.user?.name || '',
          email: billingData.user?.email || '',
          cnpj: billingData.user?.cnpj ? billingData.user.cnpj.replace(/\D/g, '') : null,
          cpf: !billingData.user?.cnpj && billingData.user?.cpf ? billingData.user.cpf.replace(/\D/g, '') : null,
          nameResponsible: billingData.user?.nameResponsible || null,
          cpfResponsible: billingData.user?.cpfResponsible ? billingData.user.cpfResponsible.replace(/\D/g, '') : null,
          emailResponsible: billingData.user?.emailResponsible || null,
          phoneResponsible: billingData.user?.phoneResponsible ? billingData.user.phoneResponsible.replace(/\D/g, '') : null,
          address: {
            street: billingData.address?.street || '',
            city: billingData.address?.city || '',
            district: billingData.address?.district || '',
            state: billingData.address?.state || '',
            zipCode: billingData.address?.zipCode ? billingData.address.zipCode.replace(/\D/g, '').replace(/(\d{5})(\d{3})/, '$1-$2') : '',
            apartmentNumber: billingData.address?.apartmentNumber || '',
            residentialNumber: billingData.address?.residentialNumber || '',
            complement: billingData.address?.complement || '',
            country: billingData.address?.country || 'Brasil',
            ibgeCityCode: billingData.address?.ibgeCityCode || null
          }
        },
        total: formatCurrencyForApi(billingData.total || 0)
      };
      
      // Adicionar dueDate se for fatura única
      if (billingData.billingType === 'single' && billingData.dueDate) {
        payload.dueDate = billingData.dueDate;
      }
      
      // Adicionar installmentSettings se for parcelado
      if (billingData.billingType === 'installments') {
        const entryType = mapEntryType(billingData.entryType);
        let value = null;
        
        if (entryType === 'PERCENT') {
          value = parseFloat(billingData.entryPercentage || 0);
        } else if (entryType === 'VALUE') {
          value = parseFloat(billingData.entryValue || 0);
        }
        
        payload.installmentSettings = {
          entryType: entryType,
          value: value,
          numberOfInstallments: parseInt(billingData.numberOfInstallments || 0),
          installments: installments,
          payableWith: billingData.paymentMethod || []
        };
      }

      const response = await billingApiService.post('', payload);
      
      if (response.data) {
        showSuccessSnackBar("Ordem de faturamento enviada com sucesso!");
        return [true, response.data];
      }
      
      return [true, response.data];
    } catch (error) {
      console.error("Erro ao enviar ordem de faturamento:", error);
      
      // O erro já foi tratado no interceptor, mas retornamos false para indicar falha
      return [false, error.response?.data || null];
    }
  }
};

export default BillingOrderService;

